<script>
  // The picked bundle's cards as a sortable table (spec "Wants" > Cards of
  // the picked bundle): Name, Set (by release date) and Price sort; Foil is
  // shown, not sorted. A set-less want shows its Sample Card's set with an
  // asterisk (CONTEXT.md "Retail Set"). On a phone the one set cell moves
  // under the name (grid areas) and Foil folds into it:
  //   <set symbol> <set name> (<finish mark><collector #>) . <Style Descriptor>
  // the two lines spanning the card image's height, the name dominant; Set
  // is not sortable there.
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import CardPrice from '../cardcollection/CardPrice.svelte';
  import SetSymbol from '../shared/SetSymbol.svelte';
  import { FINISH_LABELS } from '../../lib/logic/card-options-logic.js';

  /** @typedef {import('../../lib/logic/want-page-logic.js').WantRow} WantRow */
  /** @typedef {import('../../lib/logic/want-page-logic.js').SortKey} SortKey */

  /**
   * `addRow` is the Add a card input, drawn as the first row; `onOpen`
   * makes each row open its card's options (both the owner's Default Want
   * List only).
   * @type {{
   *   rows: WantRow[],
   *   sort: import('../../lib/logic/want-page-logic.js').SortState,
   *   onSort: (key: SortKey) => void,
   *   addRow?: import('svelte').Snippet,
   *   onOpen?: (row: WantRow) => void,
   * }}
   */
  let { rows, sort, onSort, addRow, onOpen } = $props();

  /** @param {SortKey} key */
  const ariaSort = (key) =>
    sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none';
</script>

{#snippet sortHeader(/** @type {SortKey} */ key, /** @type {string} */ label, title = '')}
  <span class="th-cell h-{key}" role="columnheader" aria-sort={ariaSort(key)}>
    <button class="th" title={title || `Sort by ${label.toLowerCase()}`} onclick={() => onSort(key)}
      >{label}{#if sort.key === key}<span class="arrow" aria-hidden="true"
          >{#if sort.dir === 1}&#9650;{:else}&#9660;{/if}</span
        >{/if}</button
    >
  </span>
{/snippet}

{#snippet name(/** @type {WantRow} */ row)}
  <span class="nm"
    >{row.name}{#if row.style}<span class="style">({row.style})</span>{/if}</span
  >
{/snippet}

<div class="table" role="table" aria-label="Wanted cards">
  <div class="row head" role="row">
    <span class="h-thumb" role="columnheader" aria-label="Image"></span>
    {@render sortHeader('name', 'Name')}
    {@render sortHeader('set', 'Set', 'Sort by set release')}
    <span class="th h-finish static" role="columnheader">Foil</span>
    {@render sortHeader('price', 'Price')}
  </div>
  {#if addRow}
    <div class="row add-row" role="row">
      <span class="add-thumb" role="cell" aria-hidden="true">+</span>
      <div class="add-cell" role="cell">{@render addRow()}</div>
    </div>
  {/if}
  {#each rows as row (row.id)}
    {@const thumb = row.scryfallId
      ? getCardImageUrl({ scryfallId: row.scryfallId }, 'small')
      : null}
    <div class="row" role="row" data-entry={row.id}>
      <span class="c-thumb" role="cell">
        {#if thumb}<img class="thumb" src={thumb} alt="" loading="lazy" />{:else}<span class="thumb"
          ></span>{/if}
      </span>
      <div class="c-name" role="cell">
        {#if onOpen}
          <!-- The whole row is the target (the button's ::after covers it). -->
          <button class="open" aria-label="Options for {row.name}" onclick={() => onOpen(row)}
            >{@render name(row)}</button
          >
        {:else}
          {@render name(row)}
        {/if}
      </div>
      <div class="c-set" role="cell">
        {#if row.set}
          <SetSymbol code={row.set.code} rarity={row.set.rarity} title={row.set.name} />
          <span class="set-name"
            ><span class="set-text">{row.set.name}</span>{#if row.set.standIn}<span
                class="stand-in"
                title="Any printing: shown by its newest set">*</span
              >{/if}</span
          >
        {/if}
        <!-- Phone only: the Foil column and the Style Descriptor join this line. -->
        {#if row.mark || row.collectorNumber}
          <span class="m-print"
            >({#if row.mark}<span class="f-icon {row.mark}" title={FINISH_LABELS[row.mark]}
              ></span>{/if}{row.collectorNumber ?? ''})</span
          >
        {/if}
        {#if row.style}<span class="m-dot" aria-hidden="true">&bull;</span><span class="m-style"
            >{row.style}</span
          >{/if}
      </div>
      <div class="c-finish" role="cell">
        {#if row.finish && FINISH_LABELS[row.finish]}
          {FINISH_LABELS[row.finish]}
        {:else}
          <span class="any">Any</span>
        {/if}
      </div>
      <div class="c-price" role="cell">
        <CardPrice price={row.price ?? 0} isSample={row.isSample} />
      </div>
    </div>
  {/each}
</div>

<style>
  .table {
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-bg-surface);
    overflow: hidden;
  }
  .row {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) minmax(0, 240px) 76px 96px;
    grid-template-areas: 'thumb name set finish price';
    align-items: center;
    gap: 12px;
    padding: 6px 10px;
  }
  .row + .row {
    border-top: 1px solid var(--color-border-subtle);
  }
  /* The owner's Default Want List: the Add a card row first, and each row
     opens its card's options. */
  .row.add-row {
    display: flex;
    align-items: center;
  }
  .add-thumb {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 50px;
    border: 1.5px dashed var(--color-text-muted);
    border-radius: 3px;
    font-size: 20px;
    color: var(--color-text-secondary);
  }
  .add-cell {
    flex: 1;
    min-width: 0;
  }
  .row:has(.open) {
    position: relative;
  }
  .row:has(.open):hover {
    background: var(--color-bg-tray);
  }
  .open {
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .open::after {
    content: '';
    position: absolute;
    inset: 0;
  }
  .row.head {
    padding-top: 4px;
    padding-bottom: 4px;
    background: var(--color-bg-tray);
  }
  .h-thumb,
  .c-thumb {
    grid-area: thumb;
  }
  .h-name,
  .c-name {
    grid-area: name;
  }
  .h-set,
  .c-set {
    grid-area: set;
  }
  .h-finish,
  .c-finish {
    grid-area: finish;
  }
  .h-price,
  .c-price {
    grid-area: price;
  }
  .th-cell {
    display: flex;
  }
  .th {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 32px;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    color: var(--color-text-tertiary);
    text-align: left;
    cursor: pointer;
  }
  .th:hover {
    color: var(--color-text);
  }
  .th.static {
    cursor: default;
  }
  .th-cell.h-price,
  .c-price {
    justify-content: flex-end;
    text-align: right;
  }
  .arrow {
    font-size: 9px;
  }
  .c-thumb {
    display: flex;
  }
  .thumb {
    display: block;
    width: 36px;
    height: 50px;
    object-fit: cover;
    border-radius: 3px;
    background: var(--color-accent-bg);
  }
  .c-name {
    min-width: 0;
  }
  .nm {
    display: block;
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .style {
    margin-left: 0.3em;
    font-weight: 400;
    color: var(--color-text-tertiary);
  }
  .c-set,
  .c-finish {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    font-size: 13px;
    color: var(--color-text-secondary);
    overflow: hidden;
    white-space: nowrap;
  }
  /* The asterisk never truncates with a long set name. */
  .set-name {
    display: flex;
    min-width: 0;
  }
  .set-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .stand-in {
    flex-shrink: 0;
    margin-left: 1px;
    color: var(--color-text-tertiary);
  }
  .any {
    color: var(--color-text-muted);
  }
  .m-print,
  .m-dot,
  .m-style {
    display: none;
  }
  /* Finish marks for the phone line: foil = the binder's rainbow dot,
     etched = a hatched metallic dot. */
  .f-icon {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .f-icon.foil {
    background: var(--foil-gradient);
  }
  .f-icon.etched {
    background: var(--etched-gradient);
  }

  @media (max-width: 768px) {
    /* Phone: the set cell moves under the name, the Foil column folds into
       it, and Set is not sortable. The two lines fill the image's height:
       the name's capitals on the image's top edge, the set line's baseline
       on its bottom edge, and the name carries more of the weight. */
    .row {
      grid-template-columns: 30px minmax(0, 1fr) auto;
      grid-template-areas:
        'thumb name price'
        'thumb set price';
      grid-template-rows: auto auto;
      column-gap: 10px;
      row-gap: 0;
    }
    .c-thumb,
    .h-thumb,
    .c-price,
    .h-price {
      align-self: center;
    }
    .c-name {
      align-self: start;
    }
    .c-set {
      align-self: end;
    }
    .c-finish,
    .h-set,
    .h-finish {
      display: none;
    }
    .thumb,
    .add-thumb {
      width: 30px;
      height: 42px;
    }
    .nm {
      font-size: 20px;
      font-weight: 700;
      line-height: 1.25;
      text-box: trim-start cap;
    }
    /* The Style Descriptor rides on the set line instead. */
    .nm .style {
      display: none;
    }
    .c-set {
      gap: 5px;
      font-size: 15px;
      font-weight: 500;
      line-height: 1.1;
      color: var(--color-text-tertiary);
      text-box: trim-end alphabetic;
      --set-symbol-size: 14px;
    }
    .m-print {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
    }
    .m-dot {
      display: inline;
      font-size: 8px;
      flex-shrink: 0;
    }
    .m-style {
      display: inline;
      flex-shrink: 0;
    }
  }
</style>
